import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/common/common-components/ui/Card";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import { Slider } from "@/app/common/common-components/ui/Slider";
import { formatNumber, formatInputNumber } from "@/app/common/common-functions/functions";
import { INTEREST_RATE } from "@/app/lib/constants";

interface BondCalculatorProps {
  countryCode?: string;
  currencySymbol?: string;
}

export const BondCalculator = ({ countryCode = "RSA", currencySymbol = "R" }: BondCalculatorProps) => {
  const [propertyValue, setPropertyValue] = useState("");
  const [deposit, setDeposit] = useState("");
  const [interestRate, setInterestRate] = useState(INTEREST_RATE);
  const [loanTerm, setLoanTerm] = useState(20);

  const handleNumberInput = (value: string, setter: (value: string) => void) => {
    // Only allow numbers and spaces
    const numericValue = value.replace(/[^0-9 ]/g, "");
    setter(numericValue);
  };

  const propertyValueNum = parseFloat(propertyValue.replace(/ /g, "")) || 0;
  const depositNum = parseFloat(deposit.replace(/ /g, "")) || 0;
  const valid = propertyValue !== "";

  const calculateBond = () => {
    if (!valid) {
      return {
        loanAmount: 0,
        monthlyPayment: 0,
        totalPayment: 0,
        totalInterest: 0,
      };
    }
    const loanAmount = propertyValueNum - depositNum;
    const monthlyInterestRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    const monthlyPayment =
      (loanAmount * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments))) / (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
    const totalPayment = monthlyPayment * numberOfPayments;
    const totalInterest = totalPayment - loanAmount;
    return {
      loanAmount,
      monthlyPayment,
      totalPayment,
      totalInterest,
    };
  };

  const { loanAmount, monthlyPayment, totalPayment, totalInterest } = calculateBond();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bond Calculator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="tw-flex tw-flex-col tw-gap-8 md:tw-flex-row">
          <div className="tw-w-full md:tw-w-1/2">
            <div className="tw-space-y-4">
              <div className="tw-space-y-2">
                <Label htmlFor="propertyValue">Property Value ({currencySymbol})</Label>
                <Input
                  id="propertyValue"
                  type="text"
                  value={formatInputNumber(propertyValue)}
                  onChange={(e) => handleNumberInput(e.target.value, setPropertyValue)}
                  placeholder="Enter property value"
                  className="tw-w-full"
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="deposit">Deposit Amount ({currencySymbol})</Label>
                <Input
                  id="deposit"
                  type="text"
                  value={formatInputNumber(deposit)}
                  onChange={(e) => handleNumberInput(e.target.value, setDeposit)}
                  placeholder="Enter deposit amount"
                  className="tw-w-full"
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="interestRate">Interest Rate (%)</Label>
                <div className="tw-flex tw-items-center tw-space-x-4">
                  <Slider
                    id="interestRate"
                    min={5}
                    max={20}
                    step={0.25}
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    className="tw-w-full"
                  />
                  <span className="tw-w-16 tw-text-right">{interestRate}%</span>
                </div>
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="loanTerm">Loan Term (Years)</Label>
                <div className="tw-flex tw-items-center tw-space-x-4">
                  <Slider id="loanTerm" min={5} max={30} step={1} value={[loanTerm]} onValueChange={(value) => setLoanTerm(value[0])} className="tw-w-full" />
                  <span className="tw-w-16 tw-text-right">{loanTerm} years</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-mt-8 tw-w-full md:tw-mt-0 md:tw-w-1/2">
            <div className="tw-space-y-4 tw-rounded-lg tw-bg-muted tw-p-4">
              <div className="tw-text-lg tw-font-semibold">Results</div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Loan Amount:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(loanAmount, 0)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Monthly Payment:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(monthlyPayment, 0)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Total Interest:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(totalInterest, 0)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Total Payment:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(totalPayment, 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
